import {
  WebGLRenderTarget,
  ShaderMaterial,
  LinearFilter,
  RGBAFormat,
} from 'three'
import { useEffect, useMemo, useRef } from 'react'
import {
  EffectComposer,
  RenderPass,
  ShaderPass,
  FXAAShader,
  BloomPass,
} from 'three-stdlib'
import { extend, useThree, useFrame } from '@react-three/fiber'

extend({ EffectComposer, ShaderPass, RenderPass })

export const NoiseEffect = () => {
  const { gl, scene, camera, size } = useThree()

  const metaballShaderPass = new ShaderPass(
    new ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
      },
      vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
        'vUv = uv;',
        'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
        '}',
      ].join('\n'),
      fragmentShader: [
        'uniform sampler2D tDiffuse;',
        'varying vec2 vUv;',
        'void main() {',
        'vec4 currentScreen = texture2D(tDiffuse, vUv);',
        'gl_FragColor = vec4(currentScreen.xyz * 1.5, currentScreen.w) * max(sign(currentScreen.w - 0.8), 0.0);',
        '}',
      ].join('\n'),
    })
  )

  const noiseShaderPass = useRef(
    new ShaderPass(
      new ShaderMaterial({
        uniforms: {
          tDiffuse: { value: null },
          amount: { value: 0 },
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix 
                * modelViewMatrix 
                * vec4( position, 1.0 );
          }
        `,
        fragmentShader: `
          uniform float amount;
          uniform sampler2D tDiffuse;
          varying vec2 vUv;
      
          float random( vec2 p ) {
            vec2 K1 = vec2(
                23.14069263277926, // e^pi (Gelfond's constant)
                2.665144142690225 // 2^sqrt(2) (Gelfondâ€“Schneider constant)
            );
            return fract( cos( dot(p,K1) ) * 12345.6789 );
          }
      
          void main() {
            vec4 color = texture2D( tDiffuse, vUv );
            vec2 uvRandom = vUv;
            uvRandom.y *= random(vec2(uvRandom.y,amount));
            color.rgb += random(uvRandom)*0.1;
            gl_FragColor = vec4( color  );
          }
      `,
      })
    )
  )

  const base = useMemo(() => {
    const offscreenTarget = new WebGLRenderTarget(size.width, size.height, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
      stencilBuffer: false,
    })

    const comp = new EffectComposer(gl, offscreenTarget)

    const renderPass = new RenderPass(scene, camera)
    const bloomPass = new BloomPass()
    const fxaaPass = new ShaderPass(FXAAShader)

    bloomPass.renderToScreen = true
    fxaaPass.material.uniforms.resolution.value.x = 1 / size.width
    fxaaPass.material.uniforms.resolution.value.y = 1 / size.height

    comp.addPass(renderPass)
    comp.addPass(metaballShaderPass)
    comp.addPass(bloomPass)
    comp.addPass(fxaaPass)
    // comp.addPass(noiseShaderPass.current)

    return comp
  }, [])

  useEffect(() => {
    base.setSize(size.width, size.height)
  }, [base, size])

  useFrame((state) => {
    noiseShaderPass.current.material.uniforms.amount.value =
      state.clock.getElapsedTime()

    base.render()
  }, 1)

  return null
}
